<script setup lang="ts">
import QRCode from 'qrcode';
import { computed, shallowRef, watch } from 'vue';
import { resolveSessionPassTheme } from './style-profile';
import { useSessionPassFonts } from './useSessionPassFonts';

interface Props {
  ticketCode: string;
  kioskName?: string;
  kioskLocation?: string;
  baseTheme?: string;
  styleProfile?: unknown;
  hasPortraits?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  kioskName: '',
  kioskLocation: '',
  baseTheme: '',
  styleProfile: undefined,
  hasPortraits: true,
});

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const descriptionKey = computed(() =>
  props.hasPortraits
    ? 'CustomerSessionPass.description'
    : 'CustomerSessionPass.descriptionNothingPrinted'
);

const theme = computed(() =>
  resolveSessionPassTheme(props.styleProfile, props.baseTheme)
);
useSessionPassFonts(theme);
const normalizedTicketCode = computed(() => props.ticketCode.toUpperCase());
const qrDataUrl = shallowRef('');
const qrUnavailable = shallowRef(false);

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const themeVariables = computed(() => ({
  '--SessionPass-background': theme.value.background,
  '--SessionPass-surface': theme.value.surface,
  '--SessionPass-surface-strong': theme.value.surfaceStrong,
  '--SessionPass-text': theme.value.text,
  '--SessionPass-text-muted': theme.value.textMuted,
  '--SessionPass-accent': theme.value.accent,
  '--SessionPass-accent-secondary': theme.value.accentSecondary,
  '--SessionPass-on-accent': theme.value.onAccent,
  '--SessionPass-display-font': theme.value.displayFont,
  '--SessionPass-body-font': theme.value.bodyFont,
  '--SessionPass-wallpaper': theme.value.wallpaperUrl
    ? `url("${theme.value.wallpaperUrl}")`
    : 'none',
}));

watch(
  [normalizedTicketCode, theme],
  async ([ticketCode, activeTheme], _previous, onCleanup) => {
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    qrDataUrl.value = '';
    qrUnavailable.value = false;
    if (!ticketCode) {
      return;
    }
    try {
      const dataUrl = await QRCode.toDataURL(ticketCode, {
        width: 512,
        margin: 3,
        errorCorrectionLevel: 'M',
        color: {
          dark: activeTheme.qrDark,
          light: activeTheme.qrLight,
        },
      });
      if (!cancelled) {
        qrDataUrl.value = dataUrl;
      }
    } catch {
      if (!cancelled) {
        qrUnavailable.value = true;
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <section
    class="CustomerSessionPass"
    data-testid="customer-session-pass"
    :data-theme="theme.id"
    :style="themeVariables"
  >
    <div class="CustomerSessionPass__wallpaper" aria-hidden="true" />
    <div class="CustomerSessionPass__content">
      <header class="CustomerSessionPass__header">
        <img
          class="CustomerSessionPass__logo"
          :src="theme.logoUrl"
          :alt="kioskName || $t('CustomerSessionPass.defaultVenue')"
        >
        <span class="CustomerSessionPass__status">
          {{ $t('CustomerSessionPass.ready') }}
        </span>
      </header>

      <div class="CustomerSessionPass__intro">
        <p class="CustomerSessionPass__eyebrow">
          {{ $t('CustomerSessionPass.eyebrow') }}
        </p>
        <h1>{{ $t('CustomerSessionPass.title') }}</h1>
        <p data-testid="customer-session-pass-description">
          {{ $t(descriptionKey) }}
        </p>
      </div>

      <div class="CustomerSessionPass__ticket">
        <p class="CustomerSessionPass__scan-label">
          {{ $t('CustomerSessionPass.scanLabel') }}
        </p>
        <div class="CustomerSessionPass__qr-frame">
          <img
            v-if="qrDataUrl"
            class="CustomerSessionPass__qr"
            data-testid="customer-session-pass-qr"
            :src="qrDataUrl"
            :alt="$t('CustomerSessionPass.qrAlt')"
          >
          <span v-else-if="qrUnavailable" class="CustomerSessionPass__qr-loading">
            {{ $t('CustomerSessionPass.unavailable') }}
          </span>
          <span v-else class="CustomerSessionPass__qr-loading" aria-live="polite">
            {{ $t('CustomerSessionPass.loading') }}
          </span>
        </div>
        <div class="CustomerSessionPass__code-block">
          <span>{{ $t('CustomerSessionPass.codeLabel') }}</span>
          <strong data-testid="customer-session-pass-code">{{ normalizedTicketCode }}</strong>
        </div>
      </div>

      <footer class="CustomerSessionPass__footer">
        <span>{{ kioskName || $t('CustomerSessionPass.defaultVenue') }}</span>
        <span v-if="kioskLocation">{{ kioskLocation }}</span>
      </footer>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.CustomerSessionPass {
  position: relative;
  isolation: isolate;
  width: min(34rem, 100%);
  margin: 0 auto clamp(1.5rem, 4vw, 3rem);
  overflow: hidden;
  border: 1px solid rgba(16, 16, 16, 0.22);
  border: 1px solid color-mix(in srgb, var(--SessionPass-text) 22%, transparent);
  border-left: 0.5rem solid var(--SessionPass-accent);
  border-radius: 0.85rem;
  color: var(--SessionPass-text);
  background: var(--SessionPass-background);
  font-family: var(--SessionPass-body-font);
  text-shadow: none;
}

.CustomerSessionPass__wallpaper {
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image: var(--SessionPass-wallpaper);
  background-position: center;
  background-size: cover;
  opacity: 0.18;
}

.CustomerSessionPass__content {
  padding: clamp(1.25rem, 4vw, 2.25rem);
  background: var(--SessionPass-background);
  background: color-mix(in srgb, var(--SessionPass-background) 90%, transparent);
}

.CustomerSessionPass__header,
.CustomerSessionPass__footer,
.CustomerSessionPass__code-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.CustomerSessionPass__logo {
  display: block;
  width: auto;
  max-width: 11rem;
  height: 2rem;
  object-fit: contain;
  object-position: left center;
}

.CustomerSessionPass__status,
.CustomerSessionPass__eyebrow,
.CustomerSessionPass__scan-label,
.CustomerSessionPass__code-block span,
.CustomerSessionPass__footer {
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.CustomerSessionPass__status,
.CustomerSessionPass__footer {
  color: var(--SessionPass-text-muted);
  font-size: 0.68rem;
}

.CustomerSessionPass__intro {
  padding: 2rem 0 1.5rem;
  border-bottom: 1px solid rgba(16, 16, 16, 0.18);
  border-bottom: 1px solid color-mix(in srgb, var(--SessionPass-text) 18%, transparent);
}

.CustomerSessionPass__eyebrow,
.CustomerSessionPass__scan-label {
  margin: 0;
  color: var(--SessionPass-accent);
  font-size: 0.72rem;
}

.CustomerSessionPass__intro h1 {
  margin: 0.45rem 0 0.75rem;
  font-family: var(--SessionPass-display-font);
  font-size: clamp(2.25rem, 9vw, 4rem);
  line-height: 1;
}

.CustomerSessionPass__intro > p:last-child {
  margin: 0;
  color: var(--SessionPass-text-muted);
  font-size: 1rem;
  line-height: 1.45;
}

.CustomerSessionPass__ticket {
  display: grid;
  justify-items: center;
  padding: 1.5rem 0 1.25rem;
}

.CustomerSessionPass__scan-label {
  margin-bottom: 0.8rem;
  color: var(--SessionPass-text-muted);
}

.CustomerSessionPass__qr-frame {
  display: grid;
  width: min(72vw, 17rem);
  aspect-ratio: 1;
  place-items: center;
  border: 1px solid rgba(16, 16, 16, 0.2);
  border: 1px solid color-mix(in srgb, var(--SessionPass-text) 20%, transparent);
  background: var(--SessionPass-surface);
}

.CustomerSessionPass__qr {
  display: block;
  width: 100%;
  height: 100%;
}

.CustomerSessionPass__qr-loading {
  color: var(--SessionPass-text-muted);
  font-size: 0.8rem;
  text-transform: uppercase;
}

.CustomerSessionPass__code-block {
  width: min(72vw, 17rem);
  margin-top: 0.9rem;
  padding: 0.8rem 0;
  border-top: 1px solid rgba(16, 16, 16, 0.18);
  border-bottom: 1px solid rgba(16, 16, 16, 0.18);
  border-block: 1px solid color-mix(in srgb, var(--SessionPass-text) 18%, transparent);
}

.CustomerSessionPass__code-block span {
  color: var(--SessionPass-text-muted);
  font-size: 0.65rem;
}

.CustomerSessionPass__code-block strong {
  overflow-wrap: anywhere;
  color: var(--SessionPass-accent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  text-align: right;
}

.CustomerSessionPass__footer {
  padding-top: 1rem;
  border-top: 1px solid rgba(16, 16, 16, 0.18);
  border-top: 1px solid color-mix(in srgb, var(--SessionPass-text) 18%, transparent);
}
</style>
